import React, { useEffect, useState } from "react";
import { NavLink, Navigate, Outlet, useLocation } from "react-router-dom";

import { useAuth } from "@mhj/shared/auth";
import UserBadge from "@mhj/shared/UserBadge";

import { ADMIN_ROLES, navItemsFor } from "../lib/adminNav";

const mobileLinkClass = ({ isActive }: { isActive: boolean }) =>
  `rounded-2xl px-4 py-3 transition ${
    isActive ? "bg-blue-100 text-blue-700" : "bg-white text-slate-600 hover:text-slate-900"
  }`;

const desktopLinkClass = ({ isActive }: { isActive: boolean }) =>
  `rounded-2xl px-4 py-3 transition ${
    isActive ? "bg-blue-100 text-blue-700" : "text-slate-600 hover:text-slate-900"
  }`;

export default function AdminShell() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!ADMIN_ROLES.has(user.role)) {
    return <Navigate to="/portal" replace />;
  }

  const navItems = navItemsFor(user.role);

  return (
    <div className="min-h-screen bg-sky-50 text-slate-900">
      <header className="sticky top-0 z-20 border-b border-sky-200 bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 py-4 sm:px-6">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-blue-600">
              Jantabo Scholarship Initiative
            </p>
            <p className="text-xs uppercase tracking-[0.3em] text-blue-600">
              Control Room
            </p>
            <h1 className="text-lg font-semibold text-slate-900">Admin Panel</h1>
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            <div className="hidden sm:block">
              <UserBadge user={user} />
            </div>
            <button
              className="hidden rounded-full border border-blue-200 bg-white px-3 py-2 text-xs uppercase tracking-[0.2em] text-slate-600 transition hover:border-blue-400 hover:text-blue-700 lg:inline-flex sm:px-4"
              onClick={() => void logout()}
            >
              Log out
            </button>
            <button
              type="button"
              className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-blue-200 bg-white text-slate-700 transition hover:border-blue-400 hover:text-blue-700 lg:hidden"
              onClick={() => setMobileMenuOpen((open) => !open)}
              aria-label={mobileMenuOpen ? "Close admin navigation" : "Open admin navigation"}
              aria-expanded={mobileMenuOpen}
              aria-controls="admin-mobile-nav"
            >
              <svg
                className="h-5 w-5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                {mobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 6l12 12M18 6L6 18" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 7h16M4 12h16M4 17h16" />
                )}
              </svg>
            </button>
          </div>
        </div>
        {mobileMenuOpen ? (
          <div
            id="admin-mobile-nav"
            className="mx-auto max-w-6xl border-t border-sky-200 px-4 py-3 sm:px-6 lg:hidden"
          >
            <nav className="grid grid-cols-1 gap-2 text-sm font-medium sm:grid-cols-2">
              {navItems.map((item) => (
                <NavLink key={item.to} to={item.to} end={item.end} className={mobileLinkClass}>
                  {item.label}
                </NavLink>
              ))}
              <button
                type="button"
                onClick={() => void logout()}
                className="rounded-2xl border border-sky-200 bg-white px-4 py-3 text-left text-slate-600 transition hover:border-blue-400 hover:text-blue-700"
              >
                Log out
              </button>
            </nav>
          </div>
        ) : null}
      </header>
      <div className="mx-auto grid max-w-6xl gap-6 px-4 py-6 sm:px-6 sm:py-8 lg:grid-cols-[260px_1fr]">
        <aside className="glass-card hidden rounded-3xl p-4 lg:block">
          <nav className="flex flex-col gap-2 text-sm font-medium">
            {navItems.map((item) => (
              <NavLink key={item.to} to={item.to} end={item.end} className={desktopLinkClass}>
                {item.label}
              </NavLink>
            ))}
          </nav>
        </aside>
        <main className="space-y-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
